import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines, FadeIn, Overline } from "@/components/motion/Reveal";
import { EditorialMarquee } from "@/components/layout/EditorialMarquee";
import { Seo } from "@/components/Seo";
import { VISION_ABOUT, MISSION_ABOUT, VALUES, METHODOLOGY, DELIVERABLES, IMAGES } from "@/constants/content";

const AboutHero = () => (
  <section data-testid="about-hero" className="pt-48 pb-24 lg:pb-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <FadeIn y={16}>
        <Overline>The Firm</Overline>
      </FadeIn>
      <MaskedLines
        className="mt-8 font-serif font-light tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-8xl"
        delay={0.15}
        lines={["Built for businesses", <span key="l2">built to <span className="italic text-primary">last.</span></span>]}
      />
      <FadeIn delay={0.5} className="mt-12 max-w-2xl">
        <p className="text-base lg:text-lg font-light text-muted-foreground leading-relaxed">
          Clear Horizon Consulting is a multidisciplinary consulting firm based in Amman, Jordan —
          founded by a team with hands-on corporate-management experience. We build the systems,
          structures, and controls that let organizations launch, operate, and scale.
        </p>
      </FadeIn>
    </div>
  </section>
);

const Chapter = ({ number, label, statement, align = "left", testId }) => (
  <div className={`grid grid-cols-1 lg:grid-cols-12 gap-12 py-24 lg:py-32 border-t border-white/5 ${align === "right" ? "" : ""}`}>
    <FadeIn className={`lg:col-span-4 ${align === "right" ? "lg:col-start-9 lg:order-2" : ""}`}>
      <span className="font-serif text-8xl lg:text-9xl font-light text-outline leading-none">{number}</span>
      <Overline className="block mt-6">{label}</Overline>
    </FadeIn>
    <FadeIn delay={0.15} className={`lg:col-span-8 ${align === "right" ? "lg:order-1" : ""}`}>
      <p data-testid={testId} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light leading-tight text-foreground">
        {statement}
      </p>
    </FadeIn>
  </div>
);

const ParallaxImage = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <div ref={ref} className="relative h-[50vh] lg:h-[70vh] overflow-hidden border-y border-white/5">
      <motion.img
        src={IMAGES.pillar1}
        alt="Abstract architecture"
        style={{ y }}
        className="absolute inset-0 w-full h-[120%] object-cover"
      />
      <div className="absolute inset-0 bg-slate-900/50" />
      <div className="absolute inset-0 flex items-center justify-center">
        <FadeIn>
          <p className="font-serif italic text-3xl sm:text-4xl lg:text-5xl font-light text-foreground/90 text-center px-6">
            "From complexity to clarity.<br />From ideas to implementation."
          </p>
        </FadeIn>
      </div>
    </div>
  );
};

const Values = () => (
  <section data-testid="values-section" className="py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <FadeIn className="mb-20">
        <Overline>Our Values</Overline>
        <h2 className="mt-6 font-serif text-4xl sm:text-5xl font-light tracking-tight">What we stand for.</h2>
      </FadeIn>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5">
        {VALUES.map((v, i) => (
          <FadeIn key={v.number} delay={i * 0.06} className="bg-background">
            <div data-testid={`value-${v.number}`} className="p-10 lg:p-12 group h-full">
              <span className="font-serif text-4xl font-light text-white/15 group-hover:text-primary/60 transition-colors duration-500">{v.number}</span>
              <h3 className="mt-6 font-serif text-2xl font-light">{v.title}</h3>
              <p className="mt-4 text-sm font-light text-muted-foreground leading-relaxed">{v.text}</p>
            </div>
          </FadeIn>
        ))}
      </div>
    </div>
  </section>
);

const Methodology = () => (
  <section data-testid="methodology-section" className="py-32 border-t border-white/5">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <FadeIn className="mb-20">
        <Overline>How We Deliver</Overline>
        <h2 className="mt-6 font-serif text-4xl sm:text-5xl font-light tracking-tight">Five steps. No loose ends.</h2>
      </FadeIn>
      <div>
        {METHODOLOGY.map((m, i) => (
          <FadeIn key={m.number} delay={i * 0.05}>
            <div data-testid={`method-${m.number}`} className="grid grid-cols-12 gap-6 py-10 border-t border-white/5 group items-baseline">
              <span className="col-span-2 lg:col-span-1 font-serif text-4xl font-light text-white/15 group-hover:text-primary/60 transition-colors duration-500">{m.number}</span>
              <h3 className="col-span-10 lg:col-span-3 font-serif text-2xl lg:text-3xl font-light">{m.title}</h3>
              <p className="col-span-10 col-start-3 lg:col-span-8 lg:col-start-5 text-sm lg:text-base font-light text-muted-foreground leading-relaxed">{m.text}</p>
            </div>
          </FadeIn>
        ))}
      </div>
      <FadeIn delay={0.2} className="mt-16 border border-primary/30 bg-primary/[0.04] p-8 lg:p-10">
        <Overline>What you get</Overline>
        <div data-testid="deliverables-chips" className="mt-6 flex flex-wrap gap-3">
          {DELIVERABLES.map((d) => (
            <span key={d} className="text-sm border border-white/15 px-5 py-2.5 text-foreground/85">
              {d}
            </span>
          ))}
        </div>
      </FadeIn>
    </div>
  </section>
);

export default function About() {
  return (
    <main data-testid="about-page">
      <Seo
        title="About Us — Clear Horizon Consulting"
        description="Who we are: a multidisciplinary consulting firm in Amman building the systems, structures, and controls that let businesses launch, operate, and scale."
        path="/about"
      />
      <h1 className="sr-only">About Clear Horizon Consulting</h1>
      <AboutHero />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Chapter number="01" label="Our Vision" statement={VISION_ABOUT} testId="about-vision-statement" />
        <Chapter number="02" label="Our Mission" statement={MISSION_ABOUT} align="right" testId="about-mission-statement" />
      </div>
      <ParallaxImage />
      <Values />
      <Methodology />
      <EditorialMarquee />
    </main>
  );
}
